/**
 * $csNetworkCall corestream network call module wrapper
 * ©2021 CoreStream
 *
 * Uses a Promise.race() wrapper to handle fetch network call rejection/success
 *
 * header and interceptors methods for fetch are in this module
 *
 * TO USE:
 * We must create a new post request as follows - this returns a guid (a simple number) that can be stored locally or on the component
 * the guid is stored on the component when we need the ability to cancel duplicate requests to kill any code
 *
 * this.networkCallGuid = this.$csNetworkCall.createFetchPostRequest(dataUrl, payload);
 * let response = await this.$csNetworkCall.fetchRequest(this.networkCallGuid);
 *
 * If we need to cancel a call, we call the below
 *
 * this.$csNetworkCall.cancelRequest(this.networkCallGuid);
 *
 * this will return undefined (Void(0)) as the response and is the only cause for an undefined response.
 *
 * if (response == undefined) // we know the call was cancelled - just silently drop through any remaining method code to kill off the logic
 *
 * If we need to force an error, we call the below, this will hit the catch block of the promise
 *
 * this.$csNetworkCall.rejectRequest(this.networkCallGuid);
 *
 * @format
 */
import auth from '@/js/auth';
import $cs_ from '@/js/plugins/cs-underscore-helpers';
import $csFileUpload from '@/js/modules/csFileUpload';

const $csNetworkCall = {};

export default $csNetworkCall;

//PRIVATE PROPERTIES
let $_networkGuid = 0; //unique guid - this is just a simple incremental counter
let $_cancelModel = {}; //promise representing the deferred cancel/reject promise
let $_abortModel = {}; // represents a fetch cancellation controller and timeout function
let $_callModel = {}; //promise representing the network call
const _fetchRequestInterceptorCallbacks = [];
const _fetchResponseInterceptorCallbacks = [];

//PRIVATE METHODS
const _csNetworkCall = {};

/**
 * @public
 * @description
 * creates an externally resolvable/rejectable promise
 * typically this would be used with Promise.race() or Promise.all() to control promise consumption
 * especially useful to resolve pending codd on beforeDestroy hooks
 */
_csNetworkCall.deferrablePromise = () => {
	let res, rej;

	let promise = new Promise((resolve, reject) => {
		res = resolve;
		rej = reject;
	});
	promise.resolve = res;
	promise.reject = rej;
	return promise;
};

/**
 * @public
 * @description
 * Checks if value is a FormData
 */
_csNetworkCall.isFormData = (data) => {
	return data instanceof FormData;
};

/**
 * @public
 * @description
 * Checks if value is a Blob
 */
_csNetworkCall.isBlob = (data) => {
	return data instanceof Blob;
};

/**
 * @public
 * @description
 * Checks if value is a File
 */
_csNetworkCall.isFile = (data) => {
	return data instanceof File;
};

/**
 * @public
 * @description
 * Checks if value is an ArrayBuffer
 */
_csNetworkCall.isArrayBuffer = (data) => {
	return data instanceof ArrayBuffer;
};

/**
 * @public
 * @description
 * Checks if value is a view on an ArrayBuffer
 */
_csNetworkCall.isArrayBufferView = (data) => {
	if (data && data.buffer != undefined) {
		return data.buffer instanceof ArrayBuffer;
	} else if (ArrayBuffer.isView) {
		return ArrayBuffer.isView(data);
	} else return false;
};

/**
 * @public
 * @description
 * Checks if value is a URLSearchParams object
 */
_csNetworkCall.isURLSearchParams = (data) => {
	return data instanceof URLSearchParams;
};

/**
 * @public
 * @description
 * Clears up an associated models like CancelModel, CallModel and AbortModel,
 * Clears up outstanding AbortModel timeout functions
 */
_csNetworkCall.clearUpModels = (guid) => {
	$_cancelModel[guid] = undefined;
	delete $_cancelModel[guid];
	$_callModel[guid] = undefined;
	delete $_callModel[guid];

	if ($_abortModel[guid] != undefined) {
		$_abortModel[guid].controller.abort();
		clearTimeout($_abortModel[guid].abortTimeout);
		$_abortModel[guid] = undefined;
		delete $_abortModel[guid];
	}
};

/**
 * @public
 * @description
 * Transforms data for the request
 * Sets a content-type header based on data type
 */
_csNetworkCall.transformData = (data, headers = {}) => {
	let body;

	if (_csNetworkCall.isFormData(data) || _csNetworkCall.isBlob(data) || _csNetworkCall.isFile(data) || _csNetworkCall.isArrayBuffer(data)) {
		body = data;
		delete headers['Content-Type'];
	} else if (_csNetworkCall.isArrayBufferView(data)) {
		body = data.buffer;
	} else if (_csNetworkCall.isURLSearchParams(data)) {
		headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=utf-8';
		body = data.toString();
	} else {
		body = JSON.stringify(data);
	}

	return body;
};

/**
 * @public
 * @description
 * Sets a default settings for the request headers, including a user authentication token
 */
_csNetworkCall.setHeaderDefaults = async (cancelAuthorisation = false) => {
	const user = await auth.getUserManager().getUser();
	const headers = {
		// 'Authorization': `${user.token_type} ${user.access_token}`,
		'Accept': 'application/json, text/plain, */*',
		'Content-Type': 'application/json'
	};

	if (!cancelAuthorisation) headers['Authorization'] = `${user.token_type} ${user.access_token}`;
	return headers;
};

/**
 * @public
 * @description
 * returns the response in the correct type from the response object
 * @param {Object} response
 * @returns {Mixed}
 */
_csNetworkCall.getResponseTypeFromFile = async (response) => {
	let contentType = response.headers.get('Content-Type');
	let data;
	
	//https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch
	//ideally it would be best to sniff json versus text versus binary response TODO! - json can be done in a try catch block using response.clone()
	if (!contentType || contentType.includes('application/x-www-form-urlencoded') || contentType.indexOf('text/') !== -1 || contentType.includes('+xml') === true) {
		data = await response.text();
	} else if (contentType.includes('application/json') === true) {
		data = await response.json();
	} else if (contentType.indexOf('application/') !== -1 || contentType.indexOf('image/') !== -1 || contentType.indexOf('video/') !== -1) {
		data = await response.blob();
	} else {
		data = await response.text();
	}
	return data;
};

/**
 * @public
 * @description
 * Settles given response and resolves promise, transfer resolved data to a generic response object (similar to the Axios)
 */
_csNetworkCall.settleResponse = async (response, guid, optionsObject) => {
	if (response != undefined && response.ok === true) {
		_csNetworkCall.responseInterceptors(response, guid, optionsObject);

		let headers = {};
		// Return an actual response header object
		let headerKeys = response.headers.keys();

		// IE11 returns these as an array, whereas Chrome etc return an iterator
		if (Array.isArray(headerKeys)) {
			for (let i = 0, iLen = headerKeys.length; i < iLen; i++) {
				if (headerKeys[i] === undefined) continue;
				headers[headerKeys[i]] = response.headers.get(headerKeys[i]);
			}
		} else {
			for (let keyValuePair of response.headers) {
				headers[keyValuePair[0]] = keyValuePair[1];
			}
		}

		let data = await _csNetworkCall.getResponseTypeFromFile(response);
		let filename = response.url?.substring(response.url.lastIndexOf('/') + 1);

		return {
			headers: headers,
			status: response.status,
			statusText: response.statusText,
			data: data,
			filename: filename //this is a placeholder for ease of use in the response
		};
	} else {
		_csNetworkCall.handleError(response, guid, optionsObject);
	}
};

/**
 * @private
 * @description
 * handles and error from a network call
 */
_csNetworkCall.handleError = (response = {message: 'Fetch Cancelled'}, guid, optionsObject) => {
	let error = new Error();
	error.response = response;
	error.message = response.status != undefined ? `Network call failed with status code: ${response.status}` : response.message;

	if (error.status == undefined && response.status != undefined) error.status = response.status;

	if (response.error == undefined) {
		response.error = error.message;
	}
	//if the abortModel exists we have a fetch request, we need to run the fetch response cancel interceptors here
	if ($_abortModel[guid] != undefined) {
		_csNetworkCall.responseInterceptors(response, guid, optionsObject);
		_csNetworkCall.clearUpModels(guid);
	} else {
		_csNetworkCall.clearUpModels(guid);
	}
	throw error;
};

/**
 * @private
 * @description
 * request fetch interceptors
 * these can be hardcoded here or we can use $csNetworkCall.addFetchInterceptorCallback method to run them as global callbacks
 */
_csNetworkCall.requestInterceptors = (config, guid, optionsObject = {}) => {
	if (optionsObject?.isBlockInterceptors !== true){
		for (let interceptor of _fetchRequestInterceptorCallbacks) {
			interceptor(config, guid);
		}
	} 
};

/**
 * @private
 * @description
 * request fetch interceptors
 * these can be hardcoded here or we can use $csNetworkCall.addFetchInterceptorCallback method to run them as global callbacks
 */
_csNetworkCall.responseInterceptors = (response, guid, optionsObject = {}) => {
	if (optionsObject?.isBlockInterceptors !== true){
		for (let interceptor of _fetchResponseInterceptorCallbacks) {
			interceptor(response, guid);
		}
	}
};

//PUBLIC METHODS

/**
 * @public
 * @description
 * Creates a POST call using ES6 fetch method. It can be cancelled(resolved) or rejected when required
 * @param { String } dataUrl
 * @param { Object } payload
 * @param { Object } optionsObject
 * @returns { Number } // The guid of the request
 */
$csNetworkCall.createFetchPostRequest = (dataUrl, payload, optionsObject = {}, cancelAuthorisation) => {
	$_networkGuid += 5;
	const guid = $_networkGuid;
	const controller = new AbortController();
	const abortTimeout = setTimeout(() => _csNetworkCall.clearUpModels(guid), optionsObject.timeout ? optionsObject.timeout : 1000 * 60);

	$_abortModel[guid] = {controller: controller, abortTimeout: abortTimeout};
	$_cancelModel[guid] = _csNetworkCall.deferrablePromise();
	$_callModel[guid] = postData(dataUrl, payload, optionsObject);

	return guid;

	async function postData(url, data = {}, options = {}) {
		const headers = await _csNetworkCall.setHeaderDefaults(cancelAuthorisation);
		if (options.headers) Object.assign(headers, options.headers);
		const body = _csNetworkCall.transformData(data, headers);
		_csNetworkCall.requestInterceptors({headers: headers}, guid, optionsObject);
		const response = await fetch(url, {method: 'POST', headers, body, signal: controller.signal});
		return _csNetworkCall.settleResponse(response, guid, optionsObject);
	}
};

/**
 * @public
 * @description
 * Creates a GET call using ES6 fetch method. It can be cancelled(resolved) or rejected when required
 * @param { String } dataUrl
 * @param { Object } optionsObject
 * @returns { Number } // The guid of the request
 */
$csNetworkCall.createFetchGetRequest = (dataUrl, optionsObject = {}) => {
	$_networkGuid += 5;
	const guid = $_networkGuid;
	const controller = new AbortController();
	const abortTimeout = setTimeout(() => _csNetworkCall.clearUpModels(guid), optionsObject.timeout ? optionsObject.timeout : 1000 * 60);

	$_abortModel[guid] = {controller: controller, abortTimeout: abortTimeout};
	$_cancelModel[guid] = _csNetworkCall.deferrablePromise();
	$_callModel[guid] = getData(dataUrl, optionsObject);

	return guid;

	async function getData(url, options = {}) {
		const headers = await _csNetworkCall.setHeaderDefaults();
		if (options.headers) Object.assign(headers, options.headers);
		_csNetworkCall.requestInterceptors({headers: headers}, guid, optionsObject);
		const response = await fetch(url, {headers, signal: controller.signal});
		return _csNetworkCall.settleResponse(response, guid, optionsObject);
	}
};

/**
 * @public
 * @description
 * Creates a POST call using ES6 fetch method. It can be cancelled(resolved) or rejected when required
 * Adds an artificial timeout to delay the call (response) - used for simulating delayed requests
 * @param { String } dataUrl
 * @param { Object } payload
 * @param { Object } optionsObject
 * @param { Number } delay
 * @returns { Number } // The guid of the request
 */
$csNetworkCall.createFetchPostRequestTimeout = (dataUrl, payload, optionsObject = {}, delay = 10000) => {
	$_networkGuid += 5;
	const guid = $_networkGuid;
	const controller = new AbortController();
	const abortTimeout = setTimeout(() => _csNetworkCall.clearUpModels(guid), optionsObject.timeout ? optionsObject.timeout : 1000 * 60);

	$_abortModel[guid] = {controller: controller, abortTimeout: abortTimeout};
	$_cancelModel[guid] = _csNetworkCall.deferrablePromise();
	$_callModel[guid] = Promise.all([postData(dataUrl, payload, optionsObject), $cs_.wait(delay)]);

	return guid;

	async function postData(url, data = {}, options = {}) {
		const headers = await _csNetworkCall.setHeaderDefaults();
		if (options.headers) Object.assign(headers, options.headers);
		const body = _csNetworkCall.transformData(data, headers);
		_csNetworkCall.requestInterceptors({headers: headers}, guid, optionsObject);
		const response = await fetch(url, {method: 'POST', headers, body, signal: controller.signal});
		return _csNetworkCall.settleResponse(response, guid, optionsObject);
	}
};

/**
 * @public
 * @description
 * Creates a GET call using ES6 fetch method. It can be cancelled(resolved) or rejected when required
 * Adds an artificial timeout to delay the call (response) - used for simulating delayed requests
 * @param { String } dataUrl
 * @param { Object } optionsObject
 * @param { Number } delay
 * @returns { Number } // The guid of the request
 */
$csNetworkCall.createFetchGetRequestTimeout = (dataUrl, optionsObject = {}, delay = 10000) => {
	$_networkGuid += 5;
	const guid = $_networkGuid;
	const controller = new AbortController();
	const abortTimeout = setTimeout(() => _csNetworkCall.clearUpModels(guid), optionsObject.timeout ? optionsObject.timeout : 1000 * 60);

	$_abortModel[guid] = {controller: controller, abortTimeout: abortTimeout};
	$_cancelModel[guid] = _csNetworkCall.deferrablePromise();
	$_callModel[guid] = Promise.all([getData(dataUrl, optionsObject), $cs_.wait(delay)]);

	return guid;

	async function getData(url, options = {}) {
		const headers = await _csNetworkCall.setHeaderDefaults();
		if (options.headers) Object.assign(headers, options.headers);
		_csNetworkCall.requestInterceptors({headers: headers}, guid, optionsObject);
		const response = await fetch(url, {headers, signal: controller.signal});
		return _csNetworkCall.settleResponse(response, guid, optionsObject);
	}
};

/**
 * @public
 * @description
 * fetches a network request based on its guid
 * returns a promise containing the request or the cancellation promise depending on which is returned first
 * @param {number} guid
 */
$csNetworkCall.fetchRequest = async (guid) => {
	try {
		let response = await Promise.race([$_callModel[guid], $_cancelModel[guid]]);

		_csNetworkCall.clearUpModels(guid);

		if (Array.isArray(response)) return response[0];
		else return response;
	} catch (error) {
		_csNetworkCall.handleError(error, guid);
	}
};

/**
 * @public
 * @description
 * cancels by resolving the promise with undefined return
 * @param {String} dataUrl
 * @returns {Void}
 */
$csNetworkCall.cancelRequest = (guid) => {
	if ($_cancelModel[guid] != undefined) $_cancelModel[guid].resolve();
};

/**
 * @public
 * @description
 * rejects a specific a network request by calling reject of the promise
 * @param {String} guid
 * @returns {Object} error object
 */
$csNetworkCall.rejectRequest = (guid) => {
	if ($_cancelModel[guid] != undefined) $_cancelModel[guid].reject();
};

/**
 * @public
 * @description
 * cancels by resolving all pending calls
 * this method also returns a promise such that we can await execution of code until all the promises are resolved, if desired
 * @returns {Promise}
 */
$csNetworkCall.cancelAll = async () => {
	let promises = [];
	for (let guid in $_cancelModel) {
		promises.push($_cancelModel[guid]);
		$_cancelModel[guid].resolve();
	}
	await Promise.all(promises);
	$_cancelModel = {};
	$_callModel = {};
	$_abortModel = {};
};

/**
 * @public
 * @description
 * rejects all pending calls
 * this method also returns a promise such that we can await execution of code until all the promises are resolved, if desired
 * @returns {Promise}
 */
$csNetworkCall.rejectAll = async () => {
	let promises = [];
	for (let guid in $_cancelModel) {
		promises.push($_cancelModel[guid]);
		$_cancelModel[guid].reject();
	}
	await Promise.all(promises);
	$_cancelModel = {};
	$_callModel = {};
	$_abortModel = {};
};

//PUBLIC interceptors methods

/**
 * @public
 * @description
 * adds a callback interceptor
 * @param {String} type 'response' | 'request'
 */
$csNetworkCall.addFetchInterceptorCallback = (type, callback) => {
	type = type.toLowerCase();
	if (type === 'request') {
		_fetchRequestInterceptorCallbacks.push(callback);
	} else if (type === 'response') {
		_fetchResponseInterceptorCallbacks.push(callback);
	}
};

//INITIALIZATION
/**
 * @public
 * @description
 * Initializes csNetworkCall
 */
$csNetworkCall.init = () => {
	$csNetworkCall.cancelAll();
	_fetchRequestInterceptorCallbacks.splice(0, _fetchRequestInterceptorCallbacks.length);
	_fetchResponseInterceptorCallbacks.splice(0, _fetchResponseInterceptorCallbacks.length);
};

/**
 *
 * @public
 * @description
 * Fetching platform settings to the module.
 * @param { Object } settings
 */
$csNetworkCall.setNetworkCallApplicationSettings = (settings) => {
	if (settings.allowedFileExtensions != undefined) $csFileUpload.initialFileExtensions(settings.allowedFileExtensions, true);
	if (settings.maxAllowedPayload != undefined) $csFileUpload.setFileMaxAllowedSize(settings.maxAllowedPayload);
};
